import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage, auth } from '../firebaseConfig';

// Re-export storage types if needed
export { storage };

// Maximum file sizes (in bytes)
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB

// Supported formats
const SUPPORTED_IMAGE_FORMATS = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const SUPPORTED_VIDEO_FORMATS = ['video/mp4', 'video/webm', 'video/quicktime'];

/**
 * Validate file before upload
 */
export const validateMediaFile = (file: File, type: 'image' | 'video'): { valid: boolean; error?: string } => {
  const maxSize = type === 'image' ? MAX_IMAGE_SIZE : MAX_VIDEO_SIZE;
  const supportedFormats = type === 'image' ? SUPPORTED_IMAGE_FORMATS : SUPPORTED_VIDEO_FORMATS;

  if (!supportedFormats.includes(file.type)) {
    return {
      valid: false,
      error: `Formato non supportato. Usa: ${type === 'image' ? 'JPEG, PNG, WebP' : 'MP4, WebM, MOV'}`
    };
  }

  if (file.size > maxSize) {
    const maxSizeMB = Math.round(maxSize / (1024 * 1024));
    return {
      valid: false,
      error: `File troppo grande. Dimensione massima: ${maxSizeMB}MB`
    };
  }

  return { valid: true };
};

/**
 * Upload image to Firebase Storage
 * 
 * IMPORTANT: This function uses Firebase SDK methods (uploadBytes, getDownloadURL)
 * which handle CORS automatically. DO NOT use XMLHttpRequest or fetch directly
 * to firebasestorage.googleapis.com as this will cause CORS errors.
 * 
 * The Firebase SDK internally handles:
 * - Authentication headers
 * - CORS preflight requests
 * - Retry logic
 * - Error handling
 */
export const uploadImage = async (file: File, userId: string, postId?: string): Promise<string> => {
  // CRITICAL: Validate file is a File instance
  if (!(file instanceof File)) {
    throw new Error('File deve essere un\'istanza di File');
  }

  // Validate file first
  if (!file || file.size === 0) {
    throw new Error('File non valido o vuoto');
  }

  const validation = validateMediaFile(file, 'image');
  if (!validation.valid) {
    throw new Error(validation.error || 'Invalid image file');
  }

  // CRITICAL: Verify userId is non-null and non-empty
  if (!userId || typeof userId !== 'string' || userId.trim().length === 0) {
    throw new Error('User ID non valido o mancante');
  }

  try {
    // CRITICAL: Verify storage is initialized and uses correct app instance
    if (!storage) {
      throw new Error('Firebase Storage non inizializzato. Verifica firebaseConfig.ts');
    }
    
    // Verify storage is connected to the correct app
    if (!storage.app) {
      throw new Error('Firebase Storage app instance mancante');
    }

    const timestamp = Date.now();
    let filename: string;
    
    if (postId === 'avatar') {
      // CRITICAL: Avatar upload - use path: users/{uid}/avatar.jpg
      // Always use .jpg extension for consistency (Firebase Storage will handle conversion if needed)
      filename = `users/${userId}/avatar.jpg`;
      console.log('[STORAGE] Avatar upload path:', filename);
    } else if (postId) {
      // Post image
      filename = `posts/${userId}/${postId}_${timestamp}_${file.name}`;
    } else {
      // Generic post image
      filename = `posts/${userId}/${timestamp}_${file.name}`;
    }
    
    console.log('[STORAGE] ===== UPLOAD START =====');
    console.log('[STORAGE] File:', file.name, file.size, 'bytes', file.type);
    console.log('[STORAGE] User ID:', userId);
    console.log('[STORAGE] Target path:', filename);
    console.log('[STORAGE] Storage bucket:', storage.app.options.storageBucket);
    console.log('[STORAGE] Storage app name:', storage.app.name);
    
    // CRITICAL: Verify bucket is configured
    const storageBucket = storage.app.options.storageBucket;
    if (!storageBucket) {
      throw new Error('Storage bucket not configured');
    }
    console.log('[STORAGE] Using bucket:', storageBucket);

    // CRITICAL: Verify Firebase Auth has current user (required for Storage auth token)
    const firebaseUser = auth.currentUser;
    if (!firebaseUser || !firebaseUser.uid) {
      const errorMsg = 'Firebase Auth currentUser è null o mancante. Devi essere autenticato per caricare file.';
      console.error('[STORAGE]', errorMsg);
      throw new Error(errorMsg);
    }

    // CRITICAL: Verify userId matches Firebase Auth uid (security)
    if (userId !== firebaseUser.uid) {
      const errorMsg = `User ID mismatch: userId (${userId}) non corrisponde a auth.currentUser.uid (${firebaseUser.uid})`;
      console.error('[STORAGE]', errorMsg);
      throw new Error(errorMsg);
    }
    
    // CRITICAL: Use ref() to create storage reference - this ensures SDK handles everything
    // DO NOT construct URLs manually - Firebase SDK handles REST calls internally
    // Pattern: ref(storage, `avatars/${uid}/${timestamp}_${filename}`)
    const storageRef = ref(storage, filename);
    console.log('[STORAGE] Storage ref created:', storageRef.fullPath);
    console.log('[STORAGE] Firebase Auth uid:', firebaseUser.uid);
    
    // CRITICAL: uploadBytes uses Firebase SDK's internal XHR with proper auth headers
    // This is NOT a manual REST call - SDK handles CORS automatically
    console.log('[STORAGE] Starting uploadBytes...');
    try {
      await uploadBytes(storageRef, file);
      console.log('[STORAGE] ✅ uploadBytes completed successfully');
    } catch (uploadError: any) {
      console.error('[STORAGE] ❌ uploadBytes FAILED:', uploadError);
      console.error('[STORAGE] Upload error code:', uploadError.code);
      console.error('[STORAGE] Upload error message:', uploadError.message);
      console.error('[STORAGE] Upload error serverResponse:', uploadError.serverResponse);
      throw uploadError;
    }
    
    // CRITICAL: getDownloadURL makes an internal REST call via SDK
    // SDK automatically adds auth token and handles CORS preflight
    // The ?name= parameter is added by SDK internally - this is NORMAL
    console.log('[STORAGE] Getting download URL...');
    let downloadURL: string;
    try {
      downloadURL = await getDownloadURL(storageRef);
      console.log('[STORAGE] ✅ Download URL obtained:', downloadURL);
    } catch (urlError: any) {
      console.error('[STORAGE] ❌ getDownloadURL FAILED:', urlError);
      console.error('[STORAGE] URL error code:', urlError.code);
      console.error('[STORAGE] URL error message:', urlError.message);
      throw urlError;
    }
    
    console.log('[STORAGE] ===== UPLOAD SUCCESS =====');
    return downloadURL;
  } catch (error: any) {
    console.error("[STORAGE] ===== UPLOAD ERROR ===== ");
    console.error("[STORAGE] Error type:", error.constructor.name);
    console.error("[STORAGE] Error code:", error.code);
    console.error("[STORAGE] Error message:", error.message);
    console.error("[STORAGE] Error stack:", error.stack);
    if (error.serverResponse) {
      console.error("[STORAGE] Server response:", error.serverResponse);
    }
    
    // Provide user-friendly error messages
    if (error.code === 'storage/unauthorized') {
      throw new Error('Non autorizzato. Verifica che le Storage Rules siano deployate e che tu sia autenticato.');
    } else if (error.code === 'storage/canceled') {
      throw new Error('Upload annullato.');
    } else if (error.code === 'storage/unknown') {
      throw new Error('Errore sconosciuto durante l\'upload. Verifica la console per dettagli.');
    } else if (error.code) {
      throw new Error(`Errore Firebase Storage (${error.code}): ${error.message}`);
    }
    
    throw new Error(`Errore durante l'upload: ${error.message || 'Unknown error'}`);
  }
};

/**
 * Upload video to Firebase Storage
 * 
 * IMPORTANT: This function uses Firebase SDK methods (uploadBytes, getDownloadURL)
 * which handle CORS automatically. DO NOT use XMLHttpRequest or fetch directly
 * to firebasestorage.googleapis.com as this will cause CORS errors.
 */
export const uploadVideo = async (file: File, userId: string, postId?: string): Promise<string> => {
  const validation = validateMediaFile(file, 'video');
  if (!validation.valid) {
    throw new Error(validation.error || 'Invalid video file');
  }

  try {
    const timestamp = Date.now();
    const filename = postId 
      ? `posts/${userId}/${postId}_${timestamp}_${file.name}`
      : `posts/${userId}/${timestamp}_${file.name}`;
    
    console.log('[STORAGE] Uploading video to:', filename);
    
    // CRITICAL: Use ref() - SDK handles everything internally
    const storageRef = ref(storage, filename);
    
    // CRITICAL: uploadBytes uses SDK's internal XHR with proper auth
    console.log('[STORAGE] Starting uploadBytes for video...');
    await uploadBytes(storageRef, file);
    console.log('[STORAGE] uploadBytes completed successfully');
    
    // CRITICAL: getDownloadURL via SDK (handles CORS automatically)
    console.log('[STORAGE] Getting download URL for video...');
    const downloadURL = await getDownloadURL(storageRef);
    console.log('[STORAGE] Download URL obtained:', downloadURL);
    
    return downloadURL;
  } catch (error: any) {
    console.error("[STORAGE] Video upload error:", error);
    console.error("[STORAGE] Error details:", {
      code: error.code,
      message: error.message,
      serverResponse: error.serverResponse
    });
    
    // Re-throw with more context
    if (error.code) {
      throw error; // Firebase error with code
    }
    throw new Error(`Failed to upload video: ${error.message || 'Unknown error'}`);
  }
};

/**
 * Delete media file from Firebase Storage
 */
export const deleteMedia = async (url: string): Promise<void> => {
  try {
    // Extract path from URL
    const urlObj = new URL(url);
    const path = decodeURIComponent(urlObj.pathname.split('/o/')[1]?.split('?')[0] || '');
    
    if (path) {
      const storageRef = ref(storage, path);
      await deleteObject(storageRef);
    }
  } catch (error) {
    console.error("Error deleting media:", error);
    // Don't throw - deletion is best effort
  }
};

